import { buildApiUrl } from '../utils/apiConfig';
import { apiClient } from "../utils/apiClient";

export interface SubscriptionLimits {
  planName: string;
  maxAthletes: number;
  currentCount: number;
  remainingSlots: number;
  unlimited: boolean;
}

/**
 * Unlimited subscriptions are flagged by the backend `unlimited` field, but also
 * store the sentinel `maxAthletes = -1` (any negative value). Treat either as
 * unlimited so a stale `unlimited:false` from an older payload never blocks adds.
 */
export const isUnlimitedLimits = (
  limits: Pick<SubscriptionLimits, 'unlimited' | 'maxAthletes'>
): boolean => limits.unlimited || limits.maxAthletes < 0;

/**
 * Whether the org has hit its athlete cap. Unlimited plans are never at the limit.
 */
export const isAtAthleteLimit = (
  limits: Pick<SubscriptionLimits, 'unlimited' | 'maxAthletes' | 'currentCount'>
): boolean => !isUnlimitedLimits(limits) && limits.currentCount >= limits.maxAthletes;

export const fetchSubscriptionLimits = async (): Promise<SubscriptionLimits> => {

  // Call the subscription limits endpoint
  const limitsUrl = buildApiUrl('api/subscriptions/limits');
  const response = await apiClient(limitsUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include' // Send cookies automatically
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch subscription limits: ${response.status}`);
  }

  const data = await response.json();

  // The API returns: { planName, maxAthletes, currentCount, remainingSlots, unlimited }
  return {
    planName: data.planName,
    maxAthletes: data.maxAthletes,
    currentCount: data.currentCount,
    remainingSlots: data.remainingSlots,
    unlimited: data.unlimited
  };
};

